import { useState, useEffect, useCallback } from 'react';

/**
 * Tətbiqin öz təsdiq/bildiriş dialoqu — brauzerin window.confirm/alert əvəzinə.
 * Hər yerdən (React-dən kənar da) çağırıla bilər:
 *
 *   import { confirm, alertDialog } from '../components/ConfirmDialog';
 *   if (!(await confirm('Silinsin?'))) return;
 *   await alertDialog('Yadda saxlanıldı.');
 *
 * String və ya obyekt qəbul edir:
 *   confirm({ title, message, confirmText, cancelText, tone: 'danger' })
 *
 * <ConfirmDialogHost /> App-da bir dəfə mount olunur.
 */

let enqueue = null;          // host mount olanda təyin olunur
const pending = [];          // host mount olmamışdan çağırılanlar

function request(mode, opts) {
    const cfg = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => {
        const item = { ...cfg, mode, resolve, _id: `${Date.now()}_${Math.round(performance.now() * 1000)}_${pending.length}` };
        if (enqueue) enqueue(item);
        else pending.push(item);
    });
}

/** Təsdiq dialoqu — Promise<boolean> qaytarır (OK → true, İmtina → false). */
export const confirm = (opts) => request('confirm', opts);

/** Bildiriş dialoqu — tək "OK" düyməsi, Promise<void> qaytarır. */
export const alertDialog = (opts) => request('alert', opts);

export default function ConfirmDialogHost() {
    const [queue, setQueue] = useState([]);

    useEffect(() => {
        enqueue = (item) => setQueue((q) => [...q, item]);
        if (pending.length) setQueue((q) => [...q, ...pending.splice(0)]);
        return () => { enqueue = null; };
    }, []);

    const dialog = queue[0] || null;

    const close = useCallback((result) => {
        if (dialog) dialog.resolve(result);
        setQueue((q) => q.slice(1));
    }, [dialog]);

    // Klaviatura: Enter → təsdiq, Esc → imtina
    useEffect(() => {
        if (!dialog) return;
        const onKey = (e) => {
            if (e.key === 'Enter') { e.preventDefault(); close(true); }
            else if (e.key === 'Escape') { e.preventDefault(); close(dialog.mode === 'alert'); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [dialog, close]);

    if (!dialog) return null;

    const isAlert = dialog.mode === 'alert';
    const danger  = dialog.tone === 'danger' || dialog.danger === true;
    const title       = dialog.title       || (isAlert ? 'Bildiriş' : 'Təsdiq');
    const confirmText = dialog.confirmText || (isAlert ? 'OK' : 'Təsdiq et');
    const cancelText  = dialog.cancelText  || 'İmtina';

    const confirmCls = danger
        ? 'bg-red-600 hover:bg-red-700'
        : 'bg-primary-600 hover:bg-primary-700';

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && close(isAlert)}
        >
            <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="px-6 pt-5 pb-2">
                    <h3 className="text-base font-bold text-gray-800">{title}</h3>
                </div>
                <div className="px-6 pb-5">
                    <p className="whitespace-pre-line text-sm leading-relaxed text-gray-600">{dialog.message}</p>
                </div>
                <div className="flex gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
                    {!isAlert && (
                        <button
                            type="button"
                            onClick={() => close(false)}
                            className="flex-1 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-gray-500 transition-all hover:bg-gray-100"
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        type="button"
                        autoFocus
                        onClick={() => close(true)}
                        className={`flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition-all ${confirmCls}`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
